import $ from 'jquery';

import ModalFactory from 'chairisher/factory/modal';

import executeRecaptcha from 'chairisher/view/helper/recaptcha';

import {
    getTradeAuthCallback,
    getQuickCreateObject,
    getRecaptchaActionName,
    isQuickCreateSignUp,
} from 'chairisher/context/auth';
import { isFunctionType } from 'chairisher/util/type';

/**
 * Manages the view layer for asynchronous authentication
 */
class AuthenticationView {
    /**
     * @param {Object=} options
     * @param {string=} options.formSelector The selector of the form contained by the modal. Default is `#js-chairish-authentication-modal-form`
     * @param {string=} options.modalSelector The selector of the modal to insert the HTML form into. Default is `#js-chairish-authentication-modal`
     * @param {boolean=} options.shouldBeDismissible Whether the modal should be dismissible
     */
    constructor({
        formSelector = '#js-chairish-authentication-modal-form',
        modalSelector = '#js-chairish-authentication-modal',
        shouldBeDismissible = true,
    } = {}) {
        this.formSelector = formSelector;
        this.modalSelector = modalSelector;
        this.shouldBeDismissible = shouldBeDismissible;

        /**
         * @type {boolean}
         */
        this.didAttemptAuthentication = false;

        /**
         * @type {boolean} True indicates the modal is displaying the sign up copy; displays log in copy when false
         */
        this.isSignUpMode = false;
    }

    /**
     * @returns {boolean}
     */
    getDidAttemptAuthentication() {
        return this.didAttemptAuthentication;
    }

    /**
     * @returns {string}
     */
    getModalSelector() {
        return this.modalSelector;
    }

    /**
     * @param {boolean} didAttemptAuthentication True indicates authentication was attempted
     */
    setDidAttemptAuthentication(didAttemptAuthentication) {
        this.didAttemptAuthentication = didAttemptAuthentication;
    }

    /**
     * Displays the account quick create form allowing people to log in or create an account.
     *
     * @param {Object} attrs - The attributes to use to display the authentication form
     * @param {string} attrs.classes - A space separated list of classes to append to the modal
     * @param {string} attrs.url - The URL of authentication form to display
     *
     * @returns {jqXHR}
     */
    display(attrs) {
        return $.ajax({ url: attrs.url }).done((data) => {
            const $modal = ModalFactory.createModal(this.getModalSelector().replace('#', ''));
            $modal.addClass(attrs.classes);
            $modal.appendTo(document.body);

            this.setFormHtml(data.authentication_form_html);

            $modal.on('hidden.bs.modal', () => {
                $modal.remove();
            });

            if (this.shouldBeDismissible) {
                $modal.modal({
                    backdrop: true,
                    keyboard: true,
                    show: false,
                });
            } else {
                const $modalHeader = $modal.find('.js-modal-header');
                $modalHeader.remove();
                $modal.modal({
                    backdrop: 'static',
                    keyboard: false,
                    show: false,
                });
            }

            $modal.modal('show');

            executeRecaptcha(getRecaptchaActionName() || 'quickcreate');
        });
    }

    /**
     * Hides the account quick create form.
     */
    hide() {
        $(this.getModalSelector()).modal('hide');
    }

    /**
     * @param {string} html The HTML to inject into the modal body
     */
    setFormHtml(html) {
        const $modal = $(this.getModalSelector());
        $modal.find('.modal-body').html(html);
        $modal.find('form').buttonLoadingHelper();

        this.isSignUpMode = isQuickCreateSignUp();

        // sign-in mode should not require terms of service agreement
        $modal.find('[name="tos_opt_in"]').prop('disabled', !this.isSignUpMode);

        $modal.find('.js-auth-toggle').on('click', 'a', (e) => {
            e.preventDefault();
            this.toggleSignUpMode();
        });
    }

    /**
     * Authenticates an account by sending its username and password to the server
     *
     * @param {string} endpoint The URL to submit the form to
     * @param {string} dataString Maps username and password fields
     * @param {Function(Object)=} successCallback Optional callback to execute on success
     * @param {Function(Object)=} errorCallback Optional callback to execute on error
     * @return $.Deferred
     */
    submit(endpoint, dataString, successCallback = null, errorCallback = null) {
        const callback = function submitCallback(data) {
            if (data.num_cart_items) {
                $('#js-cart-count').text(data.num_cart_items || '');
            }
            if (data.my_account_nav_html) {
                $('.js-account-nav').html(data.my_account_nav_html);
            }
            if (data.favorites_nav_html) {
                $('.js-favorites-nav-menu').html(data.favorites_nav_html);
            }
            if (data.is_trade) {
                const tradeAuthCallback = getTradeAuthCallback();
                if (tradeAuthCallback) {
                    tradeAuthCallback();
                }
            }
        };

        return $.ajax({
            data: `${dataString}&is_sign_up=${this.isSignUpMode}`,
            method: 'POST',
            url: endpoint,
        })
            .done((data, textStatus, jqXHR) => {
                const callbackData = { status: jqXHR.status, ...data };
                callback(callbackData);

                if (isFunctionType(successCallback)) {
                    successCallback(callbackData);
                }
            })
            .fail((jqXHR) => {
                const callbackData = { ...jqXHR.responseJSON, status: jqXHR.status };
                callback(callbackData);

                if (isFunctionType(errorCallback)) {
                    errorCallback(callbackData);
                }
                $(this.formSelector).resetLoadingButtons();
                executeRecaptcha(getRecaptchaActionName() || 'quickcreate');
            })
            .always(() => {
                this.setDidAttemptAuthentication(true);
            });
    }

    /**
     * Toggles the content of the modal to display either the appropriate "Log in" or "Sign up" copy
     */
    toggleSignUpMode() {
        const quickCreateContext = getQuickCreateObject();
        const keyPrefix = this.isSignUpMode ? 'login_' : 'sign_up_';
        const $modal = $(this.getModalSelector());

        const buttonText = quickCreateContext[`${keyPrefix}button_text`];
        const facebookButtonText = quickCreateContext[`${keyPrefix}facebook_button_text`];

        $modal.find('.js-first-name').toggleClass('hidden');
        $modal.find('.js-auth-toggle').toggleClass('hidden');
        $modal.find('.js-auth-quick-fine-print').toggleClass('hidden');
        $modal.find('.js-tos-opt-in').toggleClass('hidden');
        $modal.find('.js-is-opted-in').toggleClass('hidden');
        $modal.find('.js-intro').text(quickCreateContext[`${keyPrefix}intro_text`]);

        // $.fn.button doesn't recognize the updated data attribute unless we use .attr
        $modal.find('.js-btn-primary').text(buttonText).attr('data-loading-text', buttonText);
        $modal.find('.js-btn-facebook').text(facebookButtonText).attr('data-loading-text', facebookButtonText);

        this.isSignUpMode = !this.isSignUpMode;

        $modal.find('[name="is_opted_in"]').prop('disabled', !this.isSignUpMode);
        $modal.find('[name="tos_opt_in"]').prop('disabled', !this.isSignUpMode);
    }
}

export default AuthenticationView;
